import { FC, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { connect, ConnectedProps } from 'react-redux';
import { Store } from 'redux/root';
import 'firebase/messaging';
// redux
import { selectorGetChatUserToConnect } from 'redux/chat-service/selector';
import { selectorGetPushMessage } from 'redux/push-service/selector';
import { selectorGetUserData } from 'redux/user-service/selector';
import {
	chatClient,
	connectUserToGetStream,
	setChatUnreadMessagesCount,
	setChatUserToConnect,
} from 'redux/chat-service/actions';
import { setPushMessage } from 'redux/push-service/action';
import { getCurrentUser, setUserNotificationToken } from 'redux/user-service/actions';
import { UserToConnectType } from 'redux/chat-service/types';
// constants
import { urls } from 'core/appUrls';
import { isPublicAuthPath } from 'core/authPublicRoutes';
// functions
import { setUserPhotoSrc } from 'core/functions';
import { fireBaseGetToken, fireBaseMessageListener } from 'core/functions/firebaseNotification';
// components
import DashboardPage from 'pages/Organization/Dashboard';
import InboxPage from 'pages/Organization/Inbox';
import SnackbarInfo from 'shared/components/Snackbars/SnackarInfo';
import NotFound from 'pages/NotFound';
import SuperAdminOppoListNew from 'pages/Admin/SuperAdminOppoListNew';
import SuperAdminVolList from 'pages/Admin/SuperAdminVolList';
import SuperAdminOrgList from 'pages/Admin/SuperAdminOrgList';
import SuperAdminUserList from 'pages/Admin/SuperAdminUserList';
import SuperAdminMarketing from 'pages/Admin/SuperAdminMarketing';
import LoginSuperAdmin from 'pages/Login/LoginSuperAdmin';
import RecoveryPassword from 'pages/Organization/RecoveryPassword';
import SuperAdminResetPassword from 'pages/Login/SuperAdminResetPassword';
import AccessDenied from 'pages/AccessDenied';
import SuperLiveScreen from 'pages/Admin/SuperLiveScreen';
import SuperAdminInviteList from 'pages/Admin/SuperAdminInviteList';
import SuperAdminStoreManagement from './pages/Admin/SuperAdminStoreManagement';
import './components/translate/index';
// styles
import './App.css';

const queryClient = new QueryClient();

const App: FC<AppPropsType> = ({
	userData,
	getCurrentUser,
	setPushMessage,
	setUserNotificationToken,
	userToConnect,
	setChatUserToConnect,
	setChatUnreadMessagesCount,
	connectUserToGetStream,
}) => {
	// const [isTokenFound, setTokenFound] = useState(false);
	const history = useHistory();

	const getChatUnreadMessagesCountListener = (event: any) => {
		// eslint-disable-next-line camelcase
		const { unread_count } = event;
		// eslint-disable-next-line camelcase
		if (unread_count !== undefined) {
			// if (unread_count !== undefined && messagesUnreadCount !== unread_count) {
			setChatUnreadMessagesCount(unread_count);
		}
	};

	useEffect(() => {
		if (!userData) return;
		if (userData?.notificationTokens?.length) {
			try {
				fireBaseMessageListener()
					.then((payload: any) => {
						const { pathname, search } = window.location;
						const isCustomPushTab = pathname === urls.marketing && search.includes('custom-push');
						if (isCustomPushTab) return;

						setPushMessage({ title: payload.notification.title, body: payload.notification.body });
					})
					// eslint-disable-next-line no-console
					.catch(err => console.log('failed: ', err));
			} catch (error: any) {
				// eslint-disable-next-line no-console
				console.warn(error);
			}
		}
		if (!localStorage.getItem('pushToken')) {
			fireBaseGetToken()
				.then(token => {
					localStorage.setItem('pushToken', token as string);
					return token;
				})
				.then(token => {
					if (!userData?.notificationTokens?.some(el => el.token === token)) {
						setUserNotificationToken(token as string);
					}
				})
				.catch(err => {
					localStorage.removeItem('pushToken');
					// eslint-disable-next-line no-console
					console.log('failed: ', err);
				});
		}

		// add initialisation chat
		if (userData?.chatId) {
			// tslint:disable-next-line:no-shadowed-variable
			const userToConnect: UserToConnectType = {
				id: userData.chatId,
				name: `${userData?.firstName || 'JOC'} ${userData?.lastName || 'administrator'}`,
				image: setUserPhotoSrc(userData?.imagePath || ''),
			};
			setChatUserToConnect(userToConnect);
		}
	}, [userData?.fireBaseUserId]);

	useEffect(() => {
		const localStorageAccessToken = localStorage.getItem('accessToken');
		const { pathname } = window.location;

		if (!localStorageAccessToken && !isPublicAuthPath(pathname)) {
			history.push(urls.loginSuperAdmin);
		} else if (localStorageAccessToken) {
			getCurrentUser();
		}
	}, []);

	useEffect(() => {
		if (userToConnect && chatClient) {
			connectUserToGetStream(userToConnect);
			chatClient.on('notification.message_new', getChatUnreadMessagesCountListener);
			chatClient.on('notification.mark_read', getChatUnreadMessagesCountListener);
		}
	}, [userToConnect]);

	return (
		<QueryClientProvider client={queryClient}>
			<SnackbarInfo />
			<Switch>
				<Route exact={true} path={urls.loginSuperAdmin} component={LoginSuperAdmin} />
				<Route exact={true} path={urls.recoveryPassword} component={RecoveryPassword} />
				<Route exact={true} path={urls.resetPassword} component={SuperAdminResetPassword} />
				<Route exact={true} path={urls.main} component={() => <Redirect to={urls.loginSuperAdmin} />} />
				<Route exact={true} path={urls.inbox} component={InboxPage} />
				<Route exact={true} path={urls.dashboard} component={DashboardPage} />
				<Route exact={true} path={urls.supperAdminOppoList} component={SuperAdminOppoListNew} />
				<Route exact={true} path={urls.live} component={SuperLiveScreen} />
				<Route exact={true} path={urls.supperAdminUserList} component={SuperAdminUserList} />
				<Route exact={true} path={urls.marketing} component={SuperAdminMarketing} />
				<Route exact={true} path={urls.store} component={SuperAdminStoreManagement} />
				<Route exact={true} path={urls.supperAdminVolList} component={SuperAdminVolList} />
				<Route exact={true} path={urls.supperAdminOrgList} component={SuperAdminOrgList} />
				<Route exact={true} path={urls.supperAdminInvitList} component={SuperAdminInviteList} />
				<Route exact={true} path={urls.dashboard} component={DashboardPage} />
				<Route exact={true} path={urls.accessDenied} component={AccessDenied} />
				{/* TODO: not working */}
				<Route component={NotFound} />
			</Switch>
		</QueryClientProvider>
	);
};
const mapStateToProps = (store: Store) => ({
	userData: selectorGetUserData(store),
	initialText: selectorGetPushMessage(store),
	userToConnect: selectorGetChatUserToConnect(store),
});
const mapDispatchToProps = {
	getCurrentUser,
	setPushMessage,
	setUserNotificationToken,
	setChatUnreadMessagesCount,
	setChatUserToConnect,
	connectUserToGetStream,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type AppPropsType = ConnectedProps<typeof connector>;

export default connector(App);
